import {cpmMicrosToDollars, dollarsToCpmMicros} from './mediationDraft.ts';

export type AbFloorOverride = {
    lineId: string;
    floorPrice: number | null;
};

export function normalizeExperimentState(raw: unknown): 'RUNNING' | 'NOT_RUNNING' | 'UNKNOWN' {
    const s = String(raw || '').trim().toUpperCase();
    if (s === 'RUNNING') return 'RUNNING';
    if (s === 'NOT_RUNNING') return 'NOT_RUNNING';
    return 'UNKNOWN';
}

export function isExperimentRunning(state: string): boolean {
    return normalizeExperimentState(state) === 'RUNNING';
}

export function defaultExperimentDisplayName(groupDisplayName: string, now = new Date()): string {
    const base = String(groupDisplayName || 'group').trim() || 'group';
    const y = now.getUTCFullYear();
    const m = String(now.getUTCMonth() + 1).padStart(2, '0');
    const d = String(now.getUTCDate()).padStart(2, '0');
    return `${base}-ab-${y}${m}${d}`;
}

function lineIdOf(line: any): string {
    return String(line?.id || line?.lineId || '').trim();
}

function lineCpmMode(line: any): string {
    return String(line?.cpmMode || line?.cpm_mode || '').toUpperCase();
}

export function cloneManualFloorOverrides(upstreamLines: any[]): AbFloorOverride[] {
    const out: AbFloorOverride[] = [];
    for (const line of upstreamLines || []) {
        if (lineCpmMode(line) !== 'MANUAL') continue;
        const lineId = lineIdOf(line);
        if (!lineId) continue;
        out.push({
            lineId,
            floorPrice: cpmMicrosToDollars(line.cpmMicros ?? line.cpm_micros),
        });
    }
    return out;
}

export function buildTreatmentMediationLines(
    upstreamLines: any[],
    floorOverrides: AbFloorOverride[],
): {mediationGroupLine: Record<string, unknown>}[] {
    const floorById = new Map(
        (floorOverrides || []).map((o) => [String(o.lineId), o.floorPrice] as const),
    );
    return (upstreamLines || []).map((line) => {
        const mode = lineCpmMode(line);
        const mediationGroupLine: Record<string, unknown> = {
            displayName: String(line.displayName || line.display_name || ''),
            adSourceId: String(line.adSourceId || line.ad_source_id || ''),
            cpmMode: mode === 'LIVE' ? 'LIVE' : 'MANUAL',
            state: String(line.state || 'ENABLED').toUpperCase() === 'DISABLED' ? 'DISABLED' : 'ENABLED',
        };
        const mappings = line.adUnitMappings || line.ad_unit_mappings;
        if (mappings && typeof mappings === 'object' && Object.keys(mappings).length) {
            mediationGroupLine.adUnitMappings = {...mappings};
        }
        if (mode !== 'LIVE') {
            const id = lineIdOf(line);
            const dollars = floorById.has(id)
                ? floorById.get(id)!
                : cpmMicrosToDollars(line.cpmMicros ?? line.cpm_micros);
            const micros = dollarsToCpmMicros(dollars);
            if (micros) mediationGroupLine.cpmMicros = micros;
        }
        return {mediationGroupLine};
    });
}

export function validateCreateExperimentInput(input: {
    displayName: string;
    treatmentTrafficPercentage: unknown;
    treatmentMediationLines: unknown[];
    experimentState: string;
}): string[] {
    const errors: string[] = [];
    if (normalizeExperimentState(input.experimentState) !== 'NOT_RUNNING') {
        errors.push('仅 NOT_RUNNING 时可创建实验（当前有运行中的实验）');
    }
    if (!String(input.displayName || '').trim()) {
        errors.push('实验名称不能为空');
    }
    const pct = Number(input.treatmentTrafficPercentage);
    if (!Number.isInteger(pct) || pct < 1 || pct > 99) {
        errors.push('流量比例须为 1–99 的整数');
    }
    const n = (input.treatmentMediationLines || []).length;
    if (n < 1 || n > 200) {
        errors.push('实验组线路数量须为 1–200');
    }
    return errors;
}

export function buildCreateExperimentChange(input: {
    displayName: string;
    treatmentTrafficPercentage: unknown;
    treatmentMediationLines: {mediationGroupLine: Record<string, unknown>}[];
}): {
    action: 'create';
    displayName: string;
    treatmentTrafficPercentage: string;
    treatmentMediationLines: {mediationGroupLine: Record<string, unknown>}[];
} {
    return {
        action: 'create',
        displayName: String(input.displayName).trim(),
        treatmentTrafficPercentage: String(Number(input.treatmentTrafficPercentage)),
        treatmentMediationLines: input.treatmentMediationLines,
    };
}

export function buildStopExperimentChange(
    variantChoice: 'VARIANT_CHOICE_A' | 'VARIANT_CHOICE_B',
): {action: 'stop'; variantChoice: string} {
    return {action: 'stop', variantChoice};
}

/** Normalize upstream experimentVariant for UI partitioning. */
export function normalizeLineExperimentVariant(raw: unknown): 'VARIANT_A' | 'VARIANT_B' | 'ORIGINAL' | '' {
    const s = String(raw || '').trim().toUpperCase();
    if (s === 'VARIANT_A' || s === 'A') return 'VARIANT_A';
    if (s === 'VARIANT_B' || s === 'B') return 'VARIANT_B';
    if (s === 'ORIGINAL') return 'ORIGINAL';
    return '';
}

/**
 * Split draft rows into control (A) vs treatment (B) for the AB running view.
 * ORIGINAL / missing variant sit with A (pre-experiment lines).
 */
export function partitionDraftRowsForAbView(rows: {experimentVariant?: string}[]): {
    variantA: typeof rows;
    variantB: typeof rows;
} {
    const variantA: typeof rows = [];
    const variantB: typeof rows = [];
    for (const row of rows || []) {
        const v = normalizeLineExperimentVariant(row?.experimentVariant);
        if (v === 'VARIANT_B') {
            variantB.push(row);
        } else {
            variantA.push(row);
        }
    }
    return {variantA, variantB};
}
